import { useState, useCallback, useRef } from 'react';
import customFieldsService from '../services/customFieldsService';
import { useFacility } from '../context/FacilityContext';

/**
 * Custom field definitions for the CURRENT facility.
 *
 * `entity` scopes to 'customer' | 'unit' | 'rental'. The facility comes from
 * context rather than an argument — definitions are facility-scoped, and a
 * caller passing the wrong id would silently render another site's fields.
 */
export const useCustomFields = (entity = 'customer') => {
    const { currentFacility } = useFacility();
    const facilityId = currentFacility?._id || currentFacility?.id || '';
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const hasLoadedRef = useRef(false);

    // Fetch all definitions
    const fetchFields = useCallback(async (refresh = false) => {
        if (!refresh && hasLoadedRef.current) return fields;

        if (!facilityId) return [];
        setLoading(true);
        try {
            const { data } = await customFieldsService.getAll(facilityId, entity);
            // The API already orders by sort_order then creation; keep it.
            const sorted = Array.isArray(data) ? data : [];
            setFields(sorted);
            setError(null);
            hasLoadedRef.current = true;
            return sorted;
        } catch (err) {
            setError(err.message || 'Failed to load custom fields');
            return [];
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facilityId, entity]);

    // Group fields by category
    const getFieldsByCategory = useCallback(() => {
        const grouped = {};
        fields.forEach(field => {
            const category = field.category || 'Other';
            if (!grouped[category]) grouped[category] = [];
            grouped[category].push(field);
        });
        return grouped;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields]);

    // Validate a single value against field definition
    const validateValue = useCallback((fieldId, value) => {
        const field = fields.find(f => f.field_id === fieldId);
        if (!field) return { valid: true }; // Unknown field, ignore

        if (field.is_required && (value === null || value === '' || value === undefined)) {
            return { valid: false, message: `${field.label} is required` };
        }

        if (value) {
            // Type specific validation (basic frontend checks)
            if (field.field_type === 'number' && isNaN(Number(value))) {
                return { valid: false, message: 'Must be a number' };
            }
            if (field.field_type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
                return { valid: false, message: 'Invalid email format' };
            }

            // Rule validation
            if (field.validation_rules) {
                for (const rule of field.validation_rules) {
                    if (rule.rule_type === 'min' && Number(value) < rule.value) {
                        return { valid: false, message: rule.error_message || `Minimum value is ${rule.value}` };
                    }
                    if (rule.rule_type === 'max' && Number(value) > rule.value) {
                        return { valid: false, message: rule.error_message || `Maximum value is ${rule.value}` };
                    }
                    if (rule.rule_type === 'regex' && rule.value) {
                        try {
                            const regex = new RegExp(rule.value);
                            if (!regex.test(value)) {
                                return { valid: false, message: rule.error_message || 'Format is invalid' };
                            }
                        } catch (e) {
                            console.warn('Invalid regex rule:', rule.value);
                        }
                    }
                }
            }
        }

        return { valid: true };
    }, [fields]);

    return {
        fields,
        loading,
        error,
        fetchFields,
        getFieldsByCategory,
        validateValue
    };
};

export default useCustomFields;
